import React from "react"
import { observer } from "mobx-react-lite"
import { Button, CircularProgress } from "@mui/material"
import Field from "../../../../form/fields/Field"
import { store } from "../../../../../stores/Store"
import ErrorBoundary from "../../../../../misc/ErrorBoundary"
import AutomationCta from "./AutomationCta"
import AutomationListItem from "./AutomationListItem"
import { Automator } from "../../../../../models/Automator"
import AutomationNode from "../../../../../models/AutomationNode"
import { UTCDate } from "@date-fns/utc"
import { AppendAutomationButton } from "./AutomationReview"


interface AutomationSection {
    automationNode: AutomationNode;
    hideOldVersions?: boolean
}


export default observer(function AutomationSection ({
    automationNode,
    hideOldVersions = true
}: AutomationSection) {

    const [showOldVersions, setShowOldVersions] = React.useState(!hideOldVersions)

    var automations = [...automationNode.automations].sort((a, b) => b.run_at?.localeCompare(a.run_at))
    
    const isNewest = (automator) => {

        var newerVersion = automations.find((a) => {
            var run_at1 = new UTCDate(automator.run_at)
            var run_at2 = new UTCDate(a.run_at)
            var firstName1 = automator.profile?.subject_first_name
            var firstName2 = a.profile?.subject_first_name
            
            if (automator.id !== a.id && automator.automation_id === a.automation_id) {
                if (automator.automation_id.includes("CHILD")) {
                    return firstName1 === firstName2 && run_at1 < run_at2
                } else {
                    return run_at1 < run_at2
                }
            }
            return false
        })

        return !newerVersion
    }

    const runningAutomator = store.AutomationStore.runningAutomation?.automator
    var automationsToShow = showOldVersions ? automations : automations.filter(a => isNewest(a))

    // Remove duplicate automator if there is a more up to date runningAutomator
    if (runningAutomator) {
        automationsToShow = automationsToShow.filter(a => a.id !== runningAutomator.id)
    }

    return (
        <ErrorBoundary>
            <div className="detail-section">
                <div className="section-header">
                    <div className="header">
                        <h4>Automations</h4>

                        <Field.Checkbox 
                            label="Show old versions" 
                            className="field-checkbox-sm"
                            value={showOldVersions} 
                            warning={false} 
                            onChange={() => setShowOldVersions(!showOldVersions)} />
                    </div>
                </div>

                <AutomationCta enquiryUid={automationNode.enquiry?.uid} automationNodeUid={automationNode.uid} />

                {runningAutomator ? (
                    <div className="section-subheader">
                        {window.location.href.includes(store.AutomationStore.runningAutomation.parentUid) ? (
                            <h5>Automation in progress</h5>
                        ) : (
                            <h5>Automation in progress: <span className="link" onClick={() => store.NodeStore.navigateNode(store.AutomationStore.runningAutomation.parentUid)}>{store.AutomationStore.runningAutomation.automator?.label}</span></h5>
                        )}

                        <CircularProgress style={{width: "10px", height: "10px", margin: "0"}} />
                    </div>
                ) : null}
                
                {runningAutomator && store.NodeStore.leafNode?.uid === store.AutomationStore.runningAutomation?.parentUid ? (
                    <AutomationListItem label={runningAutomator.label} automator={runningAutomator} />
                ) : null}

                {automationsToShow?.length ? (
                    <>
                        <div className="section-subheader">
                            <h5>Previous automations</h5>
                        </div>

                        <div style={{display: "grid", gap: "15px"}}>
                            {automationsToShow.filter((childAutomator, i) => !childAutomator.profile.parent_automation_job_uid).map((automator, i) => {
                                
                                const appendedAutomations = automationsToShow.filter((childAutomator, i) => {
                                    return childAutomator.profile.parent_automation_job_uid === automator.id
                                })
                                const appendOptions = store.AutomationStore.options?.filter(option => {
                                    return option.parent_automations?.includes(automator.automation_id)
                                })
                                
                                return (
                                    <div>
                                        <AutomationListItem key={i} label={automator.label} automator={automator} />

                                        {appendOptions?.length ? (
                                            <div style={{display: "grid", gridTemplateColumns: "8px auto"}}>
                                                <div style={{marginBottom: "24.5px"}}>
                                                    <div style={{width: "1px", height: "100%", background: "#969696", marginLeft: "auto"}}></div>
                                                </div>

                                                <div>
                                                    {appendedAutomations.map((childAutomator, i) => (
                                                        <div style={{display: "grid", gridTemplateColumns: "8px auto", alignItems: "center", marginTop: "10px"}}>
                                                            <div>
                                                                <div style={{width: "100%", height: "1px", background: "#969696"}}></div>
                                                            </div>

                                                            <AutomationListItem key={i} label={childAutomator.label} automator={childAutomator} />
                                                        </div>
                                                    ))}

                                                    <div style={{display: "grid", gridTemplateColumns: "8px auto", alignItems: "center", margin: "16px 0"}}>
                                                        <div>
                                                            <div style={{width: "100%", height: "1px", background: "#969696"}}></div>
                                                        </div>

                                                        <div style={{display: "flex", gap: "10px"}}>
                                                            {appendOptions.map((option, i) => (
                                                                <AppendAutomationButton key={i} automator={automator} option={option} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>
                    </>
                ) : null}
            </div>
        </ErrorBoundary>
    )
})